import React from 'react';
import { Button, Descriptions, Card, Typography, Alert, Form, InputNumber } from 'antd';
import { CarOutlined, CheckCircleOutlined, DashboardOutlined } from '@ant-design/icons';
import { useAppSelector, useAppDispatch } from '../../../redux/hooks';
import { setCurrentMileage } from '../../../redux/reducers/vehicleSlice';

const { Title } = Typography;

interface VehicleConfirmationStepProps {
  onNext: () => void;
  onPrevious: () => void;
  isFirstStep: boolean;
  isLastStep: boolean;
}

const VehicleConfirmationStep: React.FC<VehicleConfirmationStepProps> = ({ 
  onNext, 
  onPrevious 
}) => {
  const vehicle = useAppSelector((state) => state.vehicle);
  const dispatch = useAppDispatch();
  const [form] = Form.useForm();

  const handleContinue = () => {
    form.validateFields()
      .then((values) => {
        if (values.currentMileage) {
          dispatch(setCurrentMileage(values.currentMileage));
        }
        onNext();
      })
      .catch(() => {
        // Error is handled by form validation
      });
  };

  return (
    <div className="step-container">
      <Title level={3}>Confirm Vehicle Details</Title>
      
      <Card 
        style={{ marginTop: 20 }}
        title={
          <span>
            <CarOutlined style={{ marginRight: 8 }} />
            Vehicle Information
          </span>
        }
      >
        <Descriptions column={{ xxl: 2, xl: 2, lg: 2, md: 1, sm: 1, xs: 1 }}>
          <Descriptions.Item label="Vehicle Number">
            {vehicle.vehicle?.vehicleNumber || 'N/A'}
          </Descriptions.Item>
          <Descriptions.Item label="VIN">
            {vehicle.vehicle?.vin || 'N/A'}
          </Descriptions.Item>
          <Descriptions.Item label="Year">
            {vehicle.vehicle?.year || 'N/A'}
          </Descriptions.Item>
          <Descriptions.Item label="Make">
            {vehicle.vehicle?.make || 'N/A'}
          </Descriptions.Item>
          <Descriptions.Item label="Model">
            {vehicle.vehicle?.model || 'N/A'}
          </Descriptions.Item>
          <Descriptions.Item label="Color">
            {vehicle.vehicle?.color || 'N/A'}
          </Descriptions.Item>
          <Descriptions.Item label="Status">
            {vehicle.vehicle?.isDisabled ? (
              <span style={{ color: '#ff4d4f' }}>Disabled</span>
            ) : (
              <span style={{ color: '#52c41a' }}>Active</span>
            )}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Card 
        style={{ marginTop: 20 }}
        title={
          <span>
            <DashboardOutlined style={{ marginRight: 8 }} />
            Current Mileage
          </span>
        }
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="currentMileage"
            label="Enter Current Mileage"
            rules={[
              { required: true, message: 'Current mileage is required' },
              { type: 'number', min: 0, message: 'Mileage must be a positive number' },
              {
                validator: (_, value) => {
                  if (value && vehicle.vehicle?.mileage && value < vehicle.vehicle.mileage) {
                    return Promise.reject('Current mileage cannot be less than last recorded mileage');
                  }
                  return Promise.resolve();
                }
              }
            ]}
            initialValue={vehicle.currentMileage || vehicle.vehicle?.mileage}
            help={vehicle.vehicle?.mileage ? `Last recorded: ${vehicle.vehicle.mileage.toLocaleString()} miles` : undefined}
          >
            <InputNumber
              size="large"
              style={{ width: '100%' }}
              placeholder="Enter current mileage"
              formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={(value) => parseInt(value!.replace(/\$\s?|(,*)/g, '') || '0') as any}
              min={0}
              precision={0}
            />
          </Form.Item>
        </Form>
      </Card>

      {vehicle.vehicle?.isDisabled && (
        <Alert
          message="Vehicle Status Warning"
          description="This vehicle is currently marked as disabled. Please verify this is the correct vehicle."
          type="warning"
          showIcon
          style={{ marginTop: 20 }}
        />
      )}

      <div className="step-actions" style={{ marginTop: 40 }}>
        <Button onClick={onPrevious}>
          Back to Vehicle Search
        </Button>
        <Button 
          type="primary" 
          icon={<CheckCircleOutlined />}
          onClick={handleContinue}
        >
          Confirm & Continue
        </Button>
      </div>
    </div>
  );
};

export default VehicleConfirmationStep;